import { useMutation, useQuery } from '@apollo/client';
import { Stack } from '@fluentui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { cutToast } from 'utility/toast';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import {
  ApprovalUpdate,
  ApprovalUpdateVariables,
} from 'settings/account/approvalSetup/view/__generated__/ApprovalUpdate';
import { ApprovalSetupFlowCommonData } from '../../__generated__/ApprovalSetupFlowCommonData';
import { ApprovalsInfo } from '../../__generated__/ApprovalsInfo';
import {
  FlowApproval,
  FlowApprovalVariables,
} from '../../__generated__/FlowApproval';
import { validationSchema } from '../approvalSetupValidation';
import { Footer } from '../Footer';
import { Header } from '../Header';
import { useStyles } from '../index.styles';
import { approvalFlowSidebarEnums, Sidebar } from '../Sidebar';
import { ApprovalValues } from '../types';
import { ApproverLevels } from './ApproverLevels';
import { GeneralInfo } from './GeneralInfo';
import { Testing } from './Testing';
import {
  getCreateApprovalFlowValues,
  getDefaultValues,
  getUpdateApprovalFlowValues,
} from './utils/utils';

import GET_APPROVAL_DETAILS from '../../FlowApproval.graphql';
import CREATE_APPROVAL_SETUP from './ApprovalFlowCreate.graphql';

import UPDATE_APPROVAL_SETUP from '../../../approvalSetup/view/ApprovalUpdate.graphql';

import APPROVAL_TYPE_DATA from '../../ApprovalSetupFlowCommonData.graphql';
import APPROVALS_LIST_FIELDS from '../../../approvalSetup/ApprovalsListFields.graphql';
import USER_RANK_TYPES from './UserRankTypesModal.graphql';
import {
  ApprovalFlowCreate,
  ApprovalFlowCreateVariables,
} from './__generated__/ApprovalFlowCreate';
const APPROVALS_FRAGMENT_NAME = 'ApprovalsListFields';

export const Body: React.FC<{
  onClose: () => void;
  approvalId: string | undefined;
  isNew: boolean;
  approvalsInfo: ApprovalsInfo | undefined;
  currentApprovalTypeFilter: number;
  setApprovalType: React.Dispatch<React.SetStateAction<number | undefined>>;
}> = (props) => {
  const styles = useStyles();
  const history = useHistory();
  const [menuSelected, setMenuSelected] = useState(
    approvalFlowSidebarEnums.generalInfo
  );
  const [selectedKey, setSelectedKey] = React.useState<
    'BASIC' | 'CONDITIONS' | string
  >('BASIC');
  // MAIN QUERY FOR RECORD INSERTION
  const [createApproval] = useMutation<
    ApprovalFlowCreate,
    ApprovalFlowCreateVariables
  >(CREATE_APPROVAL_SETUP, { errorPolicy: 'all' });
  const [updateApproval] = useMutation<ApprovalUpdate, ApprovalUpdateVariables>(
    UPDATE_APPROVAL_SETUP,
    {
      errorPolicy: 'all',
    }
  );

  const { data: approvalDetailsData, loading: approvalDetailsLoading } =
    useQuery<FlowApproval, FlowApprovalVariables>(GET_APPROVAL_DETAILS, {
      fetchPolicy: 'network-only',
      skip: props.isNew,
      variables: { id: props.approvalId! },
    });

  const formMethods = useForm<ApprovalValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });

  const {
    trigger,
    reset,
    handleSubmit,
    watch,
    formState: { isSubmitting, isDirty },
  } = { ...formMethods };
  const values = watch();

  useEffect(() => {
    const defaultValues = getDefaultValues(
      approvalDetailsData,
      props.isNew,
      props.currentApprovalTypeFilter
    );
    reset(defaultValues);
    trigger();
  }, [approvalDetailsData, props.isNew, reset, trigger]);

  const onCancel = () => {
    if (isDirty && !isSubmitting) {
      const confirm = window.confirm(
        'Are you sure you want to leave your changes unsaved?'
      );

      if (!confirm) return;
    }
    if (values.approvalTypeId) props.setApprovalType(values.approvalTypeId);
    props.onClose();
  };

  const { data: approvalTypes } = useQuery<ApprovalSetupFlowCommonData>(
    APPROVAL_TYPE_DATA,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-only',
    }
  );
  const onHandleSubmit = async (values: ApprovalValues) => {
    if (props.isNew) {
      const createApprovalValues = getCreateApprovalFlowValues(values);
      const isActiveCurrentApprovalId =
        props.currentApprovalTypeFilter === Number(values.approvalTypeId);
      const { errors, data } = await createApproval({
        variables: {
          input: {
            ...createApprovalValues,
          },
        },
        // need to refetch this query to get the latest status of user rank type
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: USER_RANK_TYPES,
          },
        ],
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              approvals(existing) {
                if (data?.approvalCreate?.approval) {
                  const newApprovalsRef = cache.writeFragment({
                    data: data?.approvalCreate?.approval,
                    fragment: APPROVALS_LIST_FIELDS,
                    fragmentName: APPROVALS_FRAGMENT_NAME,
                  });

                  const nodes = isActiveCurrentApprovalId
                    ? [newApprovalsRef, ...existing.nodes]
                    : existing.nodes;
                  return {
                    ...existing,
                    nodes,
                  };
                }
              },
            },
          });
        },
      });
      if (errors?.length) {
        cutToast.error(errors[0].message);
      } else {
        cutToast.success('Approval Flow created successfully');
        history.push(
          `/account-management/approval-setup?id=${data?.approvalCreate?.approval?.id}`
        );
      }
    } else {
      const updateApprovalValues = getUpdateApprovalFlowValues(
        values,
        approvalDetailsData,
        props.approvalsInfo
      );

      const { errors } = await updateApproval({
        variables: {
          input: {
            ...updateApprovalValues,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: GET_APPROVAL_DETAILS,
            variables: {
              id: props.approvalId!,
            },
          },
          // need to refetch below query to get the latest status of user rank type

          {
            query: USER_RANK_TYPES,
          },
        ],
      });

      if (errors?.length) {
        cutToast.error(errors[0].message);
      } else {
        cutToast.success('Approval flow updated successfully');
      }
    }
  };
  const isTestingClickable = !formMethods.formState.isDirty;

  return (
    <FormProvider {...formMethods}>
      <Stack>
        <Header
          approvalDetailsLoading={approvalDetailsLoading}
          isNew={props.isNew}
          approvalDetailsData={approvalDetailsData}
          onClose={onCancel}
          approvalTypes={approvalTypes}
          firstRowPriorityNumber={
            props.approvalsInfo?.approvals?.nodes?.[0]?.priorityNumber ?? 10
          }
          isTestingView={menuSelected === approvalFlowSidebarEnums.testing}
        />
        <div className={styles.mainContainer}>
          <Sidebar
            approvalDetailsData={approvalDetailsData}
            onMenuSelect={(item) => {
              if (item !== approvalFlowSidebarEnums.testing) {
                setMenuSelected(item);
              } else {
                if (isTestingClickable) {
                  setMenuSelected(item);
                }
              }
            }}
            menuSelected={menuSelected}
          />
          {menuSelected === approvalFlowSidebarEnums.generalInfo && (
            <GeneralInfo
              approvalDetailsLoading={approvalDetailsLoading}
              approvalTypes={approvalTypes}
              approvalDetailsData={approvalDetailsData}
              setSelectedKey={setSelectedKey}
              selectedKey={selectedKey}
            />
          )}
          {menuSelected === approvalFlowSidebarEnums.approvers && (
            <ApproverLevels
              approvalDetailsLoading={approvalDetailsLoading}
              approvalTypes={approvalTypes}
              approvalDetailsData={approvalDetailsData}
            />
          )}
          {menuSelected === approvalFlowSidebarEnums.testing && (
            <Testing approvalDetailsData={approvalDetailsData} />
          )}
        </div>

        <Footer
          onCancel={onCancel}
          onSave={() => {
            if (values.approvalTypeId) handleSubmit(onHandleSubmit, () => {})();
          }}
          approvalDetailsData={approvalDetailsData}
          isNew={props.isNew}
          menuSelected={menuSelected}
          selectedKey={selectedKey}
        />
      </Stack>
    </FormProvider>
  );
};
